/**
 * Show Set entity class
 */

import { has } from '../../../app/helpers/object'
import { SHOWSET_TYPES } from '../../../app/core/constants'
import { dateAsUTC } from '../../../app/helpers/date'
import { CONTENT_SET_LOCALIZATION_PAIRS, metadataMapToLocalizations, localizationsToMetadataMap } from '../../../app/helpers/localization'

/**
 * ShowSetFactory
 * returns ShowSet entity class
 *
 * @class ShowSetFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.ShowSet} class
 *
 * @ngdoc factory
 * @name mwApp.factory:ShowSetFactory
 */
/* @ngInject */
export default function ShowSetFactory ($http, $injector, Entity, FileUploadMixin, onOffMixin, alertLiteSvc) {
  /* === ShowSet class === */

  /**
   * ShowSet model
   *
   * @class ShowSet
   * @classdesc ShowSet model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.onOffMixin
   * @mixes models.FileUploadMixin
   * @return {models.ShowSet} ShowSet entity
   */
  function ShowSet () {
    const obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  ShowSet.prototype = Object.create(Entity.prototype)
  ShowSet.prototype.constructor = ShowSet

  /* === ShowSet instance methods & fields === */

  Object.assign(ShowSet.prototype, {

    /**
     * instance fields
     *
     * @memberof models.ShowSet#
     * @type {Object}
     */
    _fields: {
      id: Number,
      networkId: Number,
      networkName: String,
      name: String,
      localizationMetadata: {}, // map<langCode, {name, description, coverImageUrl}>
      quantity: Number,
      enabled: true,
      exclusivePaymentSystemSetting: {
        id: Number,
        assignedPaymentSystems: [{ paymentSystemId: Number, paymentSystemType: String }]
      },
      assignedEpisodesCount: Number,
      assignedSeasonsCount: Number,
      assignedTvShowsCount: Number,
      assignedSubscriptions: ['Subscription'],
      contentItems: [],
      coverImageUrl: String,
      description: String,
      richDescription: String,
      itemType: String,
      pricing: {
        id: Number,
        rent: {
          amount: Number,
          dueDate: String,
          paymentMethod: String,
          period: Number,
          timeUnit: String,
          $periodType: String
        },
        buy: {
          paymentMethod: String,
          amount: Number
        },
        currency: 'Currency'
      },
      $buyEnabled: Boolean,
      $rentEnabled: Boolean,
      numberOfCcu: Number,
      $overrideCCU: Boolean,
      updatedAt: String,
      updatedBy: String,
      $setType: 'tv_show',
      $itemType: 'shows'
    },

    /**
     * url template
     *
     * @memberof models.ShowSet#
     * @type {String}
     */
    _urlTemplate: 'api/networks/{{::networkId}}/tv-show-sets/{{::id}}',

    /**
     * url template for available show sets
     *
     * @memberof models.ShowSet#
     * @type {String}
     */
    _availableShowSetsTemplate: 'api/networks/{{::networkId}}/subscriptions/{{::subscriptionId}}/tv-show-sets',

    /**
     * public url template
     *
     * @memberof models.ShowSet#
     * @type {String}
     */
    _publicUrlTemplate: 'organizations/{{::networkId}}/contentSets/show/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.ShowSet#
     * @type {String}
     */
    _subscriptionUrlTemplate: 'api/networks/{{::networkId}}/subscriptions/{{::subscriptionId}}/tv-show-sets',

    /**
     * url template for loading custom prices
     *
     * @memberof models.ShowSet#
     * @type {String}
     */
    _customPricesUrlTemplate: 'api/networks/{{::networkId}}/subscriptions/{{::subscriptionId}}/tv-show-sets/{{::setId}}/prices',

    /**
     * url template for checking set intersection
     *
     * @memberof models.ShowSet#
     * @type {String}
     */
    _contentIntersectionUrlTemplate: 'api/networks/{{::networkId}}/tv-show-sets/{{::id}}/intersection',

    /**
     * On/Off Entity, performing PATCH request to the server
     *
     * @memberof models.ShowSet#
     * @param {Boolean} [enabled=this.enabled] - enable or disable Entity
     * @return {Promise} promise
     */
    _onOff: function (enabled) {
      if (typeof enabled !== 'boolean') {
        enabled = this.enabled
      }

      return $http.patch(this._url + '/status', { enabled })
        .catch(rejection => {
          this.enabled = !this.enabled
          return rejection
        })
    },

    /**
     * @description !!excluded empty seasons.
     *
     * Load tvshow extra for Show set
     *
     * @memberof models.ShowSet#
     * @returns {Promise} promise
     */
    _getAvailableShows: function (networkId, seasonSetId, url, params = {}) {
      const extraUrl = seasonSetId ? `api/networks/${networkId}/tv-show-sets/${seasonSetId}/${url}` : `api/networks/${networkId}/tv-show-sets/${url}`
      return $http.get(extraUrl, { params })
        .data()
        .then((response) => {
          for (const show of response.payload.content) {
            show.seasons = show.seasons.filter(season => season.episodes.length)
          }
          return response.payload
        })
        .catch(rejection => alertLiteSvc.alertError(rejection))
    },

    /**
     * Returns filtered page of Shows which are in Show Set
     *
     * @memberof models.ShowSet#
     * @returns {Promise} promise
     */
    _getAssignedShows: function (networkId, seasonSetId, url, params) {
      const extraUrl = seasonSetId ? `api/networks/${networkId}/tv-show-sets/${seasonSetId}/${url}` : `api/networks/${networkId}/tv-show-sets/${url}`
      return $http.get(extraUrl, { params })
        .data()
        .then((response) => response.payload)
        .catch(rejection => alertLiteSvc.alertError(rejection))
    },
    /**
     * Prepare object
     *
     * @memberof models.ChannelSet#
     * @override
     */
    _prepare: function () {
      if (!this.pricing) {
        this.pricing = {
          rent: {
            paymentMethod: 'PER_SET',
            amount: 0,
            period: 1,
            timeUnit: 'DAY'
          },
          buy: {
            paymentMethod: 'PER_SET',
            amount: 0
          }
        }
        this.$buyEnabled = true
        this.$rentEnabled = true
      } else {
        this.$buyEnabled = this.$buyEnabled === null ? !!this.pricing.buy : this.$buyEnabled
        this.$rentEnabled = this.$rentEnabled === null ? !!this.pricing.rent : this.$rentEnabled
        if (!this.$buyEnabled) {
          this.pricing.buy = {
            paymentMethod: 'PER_SET',
            amount: 0
          }
        }
        if (!this.$rentEnabled) {
          this.pricing.rent = {
            paymentMethod: 'PER_SET',
            amount: 0,
            period: 1,
            timeUnit: 'DAY'
          }
        }
      }
      if (this.pricing.rent.dueDate) {
        this.pricing.rent.$periodType = 'DUE_DATE'
        this.pricing.rent.$dueDate = new Date(this.pricing.rent.dueDate)
      } else {
        this.pricing.rent.$periodType = 'DEFAULT'
      }
      if (!this.itemType) {
        this.itemType = this.assignedEpisodesCount > 0 ? SHOWSET_TYPES.episode : this.assignedSeasonsCount > 0 ? SHOWSET_TYPES.season : SHOWSET_TYPES.tvShow
      }
      this.localizations = metadataMapToLocalizations(this.localizationMetadata, CONTENT_SET_LOCALIZATION_PAIRS)
    },

    /**
     * Get underlying object
     *
     * @memberof models.showSet#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      if (!this.$buyEnabled) {
        this.pricing.buy = null
      }

      if (!this.$rentEnabled) {
        this.pricing.rent = null
      }
      this.contentItems.forEach(ci => {
        if (this.itemType === SHOWSET_TYPES.tvShow) {
          if (!this.$buyEnabled) {
            ci.pricing.buy = null
          }
          if (!this.$rentEnabled) {
            ci.pricing.rent = null
          }
        } else {
          ci.seasons.forEach(season => {
            if (this.itemType === SHOWSET_TYPES.season) {
              if (!this.$buyEnabled) {
                season.pricing.buy = null
              }
              if (!this.$rentEnabled) {
                season.pricing.rent = null
              }
            } else {
              season.episodes.forEach(ep => {
                if (!this.$buyEnabled) {
                  ep.pricing.buy = null
                }
                if (!this.$rentEnabled) {
                  ep.pricing.rent = null
                }
              })
            }
          })
        }
      })
      if (this.pricing?.rent) {
        switch (this.pricing.rent.$periodType) {
          case 'DUE_DATE':
            this.pricing.rent.dueDate = dateAsUTC(this.pricing.rent.$dueDate, 'YYYY-MM-DD')
            this.pricing.rent.periodUnlimited = false
            this.pricing.rent.period = 0
            this.pricing.rent.timeUnit = null
            this.pricing.rent.autoPay = false
            break
          case 'DEFAULT':
            this.pricing.rent.dueDate = null
            break
          default:
            break
        }
      }
      this.localizationMetadata = localizationsToMetadataMap(this.localizations, CONTENT_SET_LOCALIZATION_PAIRS)
      return Entity.prototype._object.apply(this, arguments)
    }
  }, FileUploadMixin, onOffMixin)

  /* === ShowSet static methods & fields === */

  /**
   * Generate url
   *
   * @param uri
   * @param {Object} [o] - object over which Url will be interpolated
   * @return {String}
   */
  ShowSet.generateUrl = function (uri = '', o) {
    o = o || {}

    const cast = $injector.get(this._name) // get constructor from injector by name
    let url = cast.prototype.$_urlTemplate(o)
    url = url[url.length - 1] === '/' ? url.substring(0, url.length - 1) : url
    return url + uri
  }

  /**
   * Get banner platforms
   *
   * @memberof models.Banner#
   */
  ShowSet.getAssignedShows = function (tableState, networkId) {
    const params = {
      page: tableState.page || 0,
      count: 25
    }

    if (Object.prototype.hasOwnProperty.call(tableState, 'q')) {
      params.q = tableState.q
    }

    return $http.get(`/nora/api/networks/${networkId}/tv-show-sets/content/assigned`, { params })
      .data()
      .then((response) => response.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  ShowSet.loadSmartShowSets = function (tableState, o) {
    o = o || {}

    const url = ShowSet.generateUrl('', o)

    const params = {
      'sort-by': tableState.sort.predicate ? tableState.sort.predicate : '',
      'sort-order': tableState.sort.reverse === false ? 'asc' : 'desc',
      page: Math.floor(tableState.pagination.start / tableState.pagination.number) || 0,
      count: tableState.pagination.number
    }
    Object.assign(params, tableState.search.predicateObject)
    if (params.$) {
      params.q = params.$
      delete params.$
    }
    if (params.updatedBy) {
      params['updated-by'] = params.updatedBy
      delete params.updatedBy
    }

    if (o.searchShow) {
      params['tv-show'] = o.searchShow.id
    }
    return $http.get(url, { params })
      .then(
        (response) => {
          const data = has('payload')(response.data)
            ? response.data.payload
            : response.data

          const result = this.cast(data, {
            // response structure
            content: [this._name],
            first: true,
            last: true,
            number: 0,
            numberOfElements: 0,
            size: 0,
            sort: [{
              ascending: true,
              direction: '',
              ignoreCase: true,
              nullHandling: '',
              property: ''
            }],
            totalElements: 0,
            totalPages: 0
          })

          // change SmartTable state
          if (Array.isArray(result.sort) && result.sort.length) {
            const last = result.sort.pop()
            tableState.sort.predicate = last.property
            tableState.sort.reverse = !last.ascending
          }
          tableState.pagination.start = result.number * result.size // first element
          tableState.pagination.number = result.size // page size
          tableState.pagination.numberOfPages = result.totalPages
          tableState.pagination.totalItemCount = result.totalElements

          // return array for SmartTable
          return result
        }
      ).catch(alertLiteSvc.alertError)
  }

  ShowSet.loadShowSets = function (tableState, o) {
    o = o || {}

    const cast = $injector.get(this._name) // get constructor from injector by name
    let url = o.subscriptionId ? cast.prototype.$_availableShowSetsTemplate(o) : cast.prototype.$_urlTemplate(o)
    url = url[url.length - 1] === '/' ? url.substring(0, url.length - 1) : url

    const params = {
      page: tableState.page || 0,
      count: tableState.count,
      q: tableState.name,
      available: ''
    }
    return $http.get(url, { params })
      .then(
        (response) => {
          const data = has('payload')(response.data)
            ? response.data.payload
            : response.data

          const result = this.cast(data, {
            // response structure
            content: [cast._name],
            last: true,
            number: 0,
            numberOfElements: 0,
            size: 0,
            totalElements: 0,
            totalPages: 0
          })

          tableState.page = result.number
          tableState.count = result.size // page size

          return result
        }
      ).catch(alertLiteSvc.alertError)
  }

  // copy all static methods from Entity class
  Object.assign(ShowSet, Entity)
  ShowSet._name = 'ShowSet'
  ShowSet._publicName = 'TV Show Set'
  ShowSet.init()

  // permissions
  ShowSet.access = {
    view: '',
    create: '',
    edit: '',
    delete: ''
  }

  return ShowSet
}
